import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box, Grid, Typography, Paper, Chip, Tooltip,
  CircularProgress, Alert, Stack, Button, ButtonBase,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import PrintIcon            from '@mui/icons-material/Print';
import ArrowBackIcon        from '@mui/icons-material/ArrowBack';
import ChevronRightIcon     from '@mui/icons-material/ChevronRight';
import BarChartIcon         from '@mui/icons-material/BarChart';
import ReceiptLongIcon      from '@mui/icons-material/ReceiptLong';
import apiService from '../services/apiServices';
import { buildEmailHTML } from '../components/salaries/SalariesList';
import { downloadPayslipPdf } from '../utils/payslipPdf';

const fmt = (v) =>
  new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v || 0);
const fmtShort = (v) => '€' + Math.round(v || 0).toLocaleString('sq-AL');
// Plain 2-decimal number for table cells ("-" when empty), like the reference sheet.
const fmt2 = (v) => (v ? Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-');

const MONTH_FULL = ['', 'Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];

const C = {
  brand:   '#3E8FA3',
  neto:    '#10B981',
  tax:     '#F59E0B',
  pension: '#7C3AED',
  advance: '#EA580C',
  penalty: '#EF4444',
  health:  '#0EA5E9',
  bar:     '#E2E8F0',
  barSel:  '#3E8FA3',
};

// ─── KPI tile ─────────────────────────────────────────────────────────────────
function Kpi({ label, value, color, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, height: '100%', bgcolor: 'background.paper' }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: color || 'text.primary', lineHeight: 1.15, mt: 0.5 }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>}
    </Paper>
  );
}

// ─── Email-style payslip rendered in an isolated frame (identical to the email) ─
const PayslipFrame = React.forwardRef(function PayslipFrame({ html }, ref) {
  const [h, setH] = useState(640);
  return (
    <Box sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <iframe
        ref={ref}
        title="Fletëpagesa"
        srcDoc={html}
        width="100%"
        onLoad={(e) => { try { setH((e.target.contentWindow.document.body.scrollHeight || 640) + 16); } catch (_) { /* same-origin guard */ } }}
        style={{ width: '100%', border: 'none', display: 'block', height: h, background: '#F3F4F6' }}
      />
    </Box>
  );
});

// ─── Month "bar box" — a proportional fill with the month number (ref layout) ─
function MonthBar({ pct, num, active, has }) {
  return (
    <Box sx={{
      position: 'relative', width: 76, height: 36, borderRadius: 1, flexShrink: 0,
      border: '1px solid', borderColor: active ? C.brand : '#cfe0ea',
      bgcolor: '#eef4f9', overflow: 'hidden',
    }}>
      {has && (
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: 'linear-gradient(180deg,#7db8d8 0%,#3E8FA3 100%)' }} />
      )}
      <Typography sx={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1,
        fontSize: '1.1rem', fontWeight: 800,
        color: has && pct > 55 ? '#fff' : (has ? '#1f3a48' : '#9bb3c2'),
      }}>
        {num}
      </Typography>
    </Box>
  );
}

// ─── 12-month panel — 2 columns (1–6 | 7–12), mirroring the reference card ────
function MonthGrid({ year, monthSlips, maxNet, selMonth, onSelect }) {
  // Interleave so the left column is months 1–6 and the right column is 7–12.
  const order = [1, 7, 2, 8, 3, 9, 4, 10, 5, 11, 6, 12];
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: C.brand }}>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
          Pagat për muajin · {year}
        </Typography>
      </Box>
      <Grid container>
        {order.map((m, idx) => {
          const s = monthSlips[m];
          const has = !!s;
          const net = s?.NetSalary || 0;
          const pct = has && maxNet ? Math.max(8, (net / maxNet) * 100) : 0;
          const active = has && m === selMonth;
          return (
            <Grid
              item xs={12} sm={6} key={m}
              sx={{
                borderBottom: '1px solid', borderColor: 'divider',
                ...(idx % 2 === 0 ? { borderRight: { sm: '1px solid' }, borderRightColor: { sm: 'divider' } } : {}),
              }}
            >
              <ButtonBase
                disabled={!has}
                onClick={() => has && onSelect(m)}
                sx={{
                  width: '100%', justifyContent: 'flex-start', gap: 1.5, px: 1.75, py: 1.1,
                  opacity: has ? 1 : 0.55, cursor: has ? 'pointer' : 'default',
                  bgcolor: active ? 'rgba(62,143,163,0.08)' : 'transparent',
                  transition: 'background-color .15s',
                  '&:hover': { bgcolor: has ? (active ? 'rgba(62,143,163,0.12)' : 'grey.50') : 'transparent' },
                }}
              >
                <MonthBar pct={pct} num={m} active={active} has={has} />
                <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, color: has ? C.brand : 'text.disabled', lineHeight: 1.15 }}>
                    {MONTH_FULL[m]}
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                    {has ? fmt(net) : '—'}
                  </Typography>
                </Box>
                {active && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.brand, mr: 0.5 }} />}
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
}

// ─── Full-year statistics — complete monthly breakdown + yearly total ────────
// Every pay component per month: payments (bruto, bazë, bonus, orë shtesë),
// deductions (tatim, pension, sig. shëndetësor, avanci, penalitete), employer
// contributions, taxable income, taxes, net — plus the note. Scrollable.
const STAT_COLS = [
  { k: 'bruto',     label: 'Bruto' },
  { k: 'baze',      label: 'Bazë' },
  { k: 'bonus',     label: 'Bonus' },
  { k: 'ore',       label: 'Orë shtesë' },
  { k: 'tatim',     label: 'Tatim' },
  { k: 'pensPu',    label: 'Pension (pu)' },
  { k: 'pensPun',   label: 'Pension (pun)' },
  { k: 'shPu',      label: 'Sig.sh (pu)' },
  { k: 'shPun',     label: 'Sig.sh (pun)' },
  { k: 'avanci',    label: 'Avanci' },
  { k: 'penal',     label: 'Penalitete' },
  { k: 'kontrib',   label: 'Gjithsej kontributet' },
  { k: 'tatueshme', label: 'Të ardhura e tatueshme' },
  { k: 'taksat',    label: 'Gjithsej taksat' },
  { k: 'neto',      label: 'Paga neto', net: true },
];

function YearStats({ year, monthSlips, selMonth, onSelect }) {
  // One row per month (Jan–Dec); empty months render as "-".
  const rows = Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    const s = monthSlips[m];
    if (!s) return { m, has: false };
    const bruto   = s.GrossSalary || s.Salary || 0;
    const pensPu  = s.PensionEmployee || 0;
    const pensPun = s.PensionEmployer || 0;
    const shPu    = s.HealthInsEmployee || 0;
    const shPun   = s.HealthInsEmployer || 0;
    const kEmp    = pensPu + shPu;
    const r = {
      m, has: true, note: s.Note || '',
      bruto, baze: s.BaseSalary || 0, bonus: s.Bonus || 0, ore: s.HourSalary || 0,
      tatim: s.Tax || 0, pensPu, pensPun, shPu, shPun,
      avanci: s.Advances || 0, penal: s.Penalties || 0,
      kontrib: kEmp + pensPun + shPun,
      tatueshme: bruto - kEmp,
      taksat: s.Tax || 0,
      neto: s.NetSalary || 0,
    };
    return r;
  });
  const monthsPaid = rows.filter((r) => r.has).length;
  const tot = {};
  STAT_COLS.forEach(({ k }) => { tot[k] = rows.reduce((a, r) => a + (r.has ? r[k] : 0), 0); });
  const hasNotes = rows.some((r) => r.has && r.note);

  const numCell = (v, { strong, net } = {}) => (
    <TableCell align="right" sx={{
      fontFamily: 'monospace', whiteSpace: 'nowrap', px: 1,
      fontSize: net ? '0.8rem' : '0.76rem',
      fontWeight: strong || net ? 800 : 500,
      color: net ? C.brand : (v ? 'text.primary' : 'text.disabled'),
    }}>{fmt2(v)}</TableCell>
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, mt: 3, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: C.brand, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
            Të dhëna statistikore · viti {year}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem' }}>
            Pagesat, ndalesat dhe kontributet — për çdo muaj dhe gjithsej për vitin
          </Typography>
        </Box>
        <Chip size="small" label={`${monthsPaid} muaj me pagë`} sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontSize: '0.68rem', fontWeight: 700 }} />
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.9rem', position: 'sticky', left: 0, bgcolor: 'grey.100', zIndex: 1 }}>{year}</TableCell>
              {STAT_COLS.map((c) => (
                <TableCell key={c.k} align="right" sx={{ fontWeight: 800, fontSize: '0.68rem', lineHeight: 1.2, whiteSpace: 'normal', minWidth: 64, px: 1 }}>{c.label}</TableCell>
              ))}
              {hasNotes && <TableCell sx={{ fontWeight: 800, fontSize: '0.68rem' }}>Shënim</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const active = r.has && r.m === selMonth;
              return (
                <TableRow
                  key={r.m}
                  hover={r.has}
                  onClick={() => r.has && onSelect(r.m)}
                  sx={{ cursor: r.has ? 'pointer' : 'default', bgcolor: active ? 'rgba(62,143,163,0.10)' : 'inherit' }}
                >
                  <TableCell sx={{ fontWeight: active ? 800 : 600, fontSize: '0.82rem', color: r.has ? 'text.primary' : 'text.disabled', position: 'sticky', left: 0, bgcolor: active ? '#eaf3f6' : 'background.paper', zIndex: 1 }}>
                    {MONTH_FULL[r.m]}
                  </TableCell>
                  {r.has
                    ? STAT_COLS.map((c) => <React.Fragment key={c.k}>{numCell(r[c.k], { net: c.net })}</React.Fragment>)
                    : STAT_COLS.map((c) => <TableCell key={c.k} align="right" sx={{ color: 'text.disabled' }}>-</TableCell>)}
                  {hasNotes && (
                    <TableCell sx={{ fontSize: '0.74rem', color: 'text.secondary', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.has && r.note ? <Tooltip title={r.note}><span>{r.note}</span></Tooltip> : '—'}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {/* Yearly total */}
            <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: C.brand, bgcolor: 'rgba(62,143,163,0.08)' } }}>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.82rem', position: 'sticky', left: 0, bgcolor: '#e4eff2', zIndex: 1 }}>Për vitin:</TableCell>
              {STAT_COLS.map((c) => <React.Fragment key={c.k}>{numCell(tot[c.k], { strong: true, net: c.net })}</React.Fragment>)}
              {hasNotes && <TableCell />}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

// Shared loader for the logged-in worker's payslips.
function useMyPayslips() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payslips, setPayslips] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const json = await apiService().get('my-payslips');
        if (cancelled) return;
        if (json.success && Array.isArray(json.data)) setPayslips(json.data);
        else setError(json.errorMessage || json.error || 'Nuk u gjetën të dhëna pagese.');
      } catch {
        if (!cancelled) setError('Gabim rrjeti — provoni përsëri.');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  return { loading, error, payslips };
}

// ─── Main page — dashboard only (clicking a month opens its own page) ─────────
export default function MyPayslipsPage() {
  const history = useHistory();
  const { loading, error, payslips } = useMyPayslips();
  const [year, setYear] = useState(null);

  const years = useMemo(
    () => [...new Set(payslips.map((s) => s.Year).filter(Boolean))].sort((a, b) => b - a),
    [payslips],
  );
  useEffect(() => {
    if (years.length && (year == null || !years.includes(year))) setYear(years[0]);
  }, [years, year]);

  const yearSlips  = useMemo(() => payslips.filter((s) => s.Year === year), [payslips, year]);
  const monthSlips = useMemo(() => {
    const map = {};
    yearSlips.forEach((s) => { if (s.Month) map[s.Month] = s; });
    return map;
  }, [yearSlips]);
  const maxNet = useMemo(() => Math.max(0, ...yearSlips.map((s) => s.NetSalary || 0)), [yearSlips]);

  // Clicking a month opens its own payslip page.
  const openMonth = (m) => history.push(`/my-payslips/${year}/${m}`);

  const kpis = useMemo(() => {
    const net = yearSlips.reduce((a, s) => a + (s.NetSalary || 0), 0);
    const gross = yearSlips.reduce((a, s) => a + (s.GrossSalary || s.Salary || 0), 0);
    return { count: yearSlips.length, net, gross, avg: yearSlips.length ? net / yearSlips.length : 0 };
  }, [yearSlips]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1160, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(62,143,163,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ReceiptLongIcon sx={{ color: C.brand, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Pagat e mia</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Paneli i pagave — kliko një muaj për fletëpagesën e tij
            </Typography>
          </Box>
        </Stack>
        {years.length > 1 && (
          <Stack direction="row" spacing={0.75}>
            {years.map((y) => (
              <Chip key={y} label={y} size="small" onClick={() => setYear(y)}
                color={y === year ? 'primary' : 'default'} variant={y === year ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, cursor: 'pointer' }} />
            ))}
          </Stack>
        )}
      </Stack>

      {error && <Alert severity="info" sx={{ fontSize: '0.82rem', mb: 2 }}>{error}</Alert>}

      {!error && !payslips.length && (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Nuk ka të dhëna pagese</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
            Fletëpagesat do të shfaqen këtu sapo të finalizohen nga departamenti i pagave.
          </Typography>
        </Paper>
      )}

      {!error && payslips.length > 0 && (
        <>
          <Grid container spacing={1.75} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><Kpi label={`Neto ${year || ''}`} value={fmtShort(kpis.net)} color={C.neto} sub={`${kpis.count} pagesa`} /></Grid>
            <Grid item xs={6} sm={3}><Kpi label={`Bruto ${year || ''}`} value={fmtShort(kpis.gross)} color={C.brand} /></Grid>
            <Grid item xs={6} sm={3}><Kpi label="Mesatare / muaj" value={fmtShort(kpis.avg)} color="#2563EB" /></Grid>
            <Grid item xs={6} sm={3}><Kpi label="Pagesa gjithsej" value={payslips.length} color={C.pension} /></Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <MonthGrid year={year} monthSlips={monthSlips} maxNet={maxNet} selMonth={null} onSelect={openMonth} />
          </Box>

          {/* Statistics — a clickable tab that opens the full yearly report */}
          {yearSlips.length > 0 && (
            <ButtonBase
              onClick={() => history.push(`/my-payslips/stats/${year}`)}
              sx={{ width: '100%', display: 'block', textAlign: 'left', borderRadius: 2.5 }}
            >
              <Paper variant="outlined" sx={{
                borderRadius: 2.5, overflow: 'hidden',
                transition: 'box-shadow .15s, border-color .15s, transform .12s',
                '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.10)', borderColor: C.brand, transform: 'translateY(-1px)' },
              }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: C.brand, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <BarChartIcon sx={{ color: '#fff' }} />
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
                        Të dhëna statistikore · viti {year}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem' }}>
                        Kliko për raportin e plotë vjetor — pagesat, ndalesat dhe kontributet
                      </Typography>
                    </Box>
                  </Stack>
                  <ChevronRightIcon sx={{ color: '#fff' }} />
                </Box>
                <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
                  <Box><Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Neto vjetore</Typography><Typography sx={{ fontWeight: 800, color: C.neto }}>{fmtShort(kpis.net)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Bruto vjetore</Typography><Typography sx={{ fontWeight: 800, color: C.brand }}>{fmtShort(kpis.gross)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Muaj me pagë</Typography><Typography sx={{ fontWeight: 800 }}>{kpis.count}</Typography></Box>
                </Box>
              </Paper>
            </ButtonBase>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Single month payslip — its own page (opened from the dashboard) ──────────
export function MyPayslipDetailPage() {
  const history = useHistory();
  const { year, month } = useParams();
  const { loading, error, payslips } = useMyPayslips();

  const y = Number(year);
  const m = Number(month);
  const slip = useMemo(
    () => payslips.find((s) => Number(s.Year) === y && Number(s.Month) === m) || null,
    [payslips, y, m],
  );

  // Company info for the payslip header (same document as the email).
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    apiService().get('settings').then((r) => {
      if (!cancelled && r?.success && r.data?.[0]) setSettings(r.data[0]);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const company = useMemo(() => (settings ? {
    name:    settings.CompanyName || settings.company_name || '',
    nui:     settings.FiscalNumber || settings.NUI || settings.nui || '',
    address: settings.Address || settings.address || '',
    city:    settings.City || settings.city || '',
  } : ''), [settings]);

  // The EXACT document emailed to the worker (buildEmailHTML from SalariesList).
  const html = useMemo(
    () => (slip ? buildEmailHTML(slip, slip.Period || `${MONTH_FULL[m] || month} ${year}`, company, { showZero: true }) : ''),
    [slip, company, m, month, year],
  );

  // Download the same Treasury-style PDF that is emailed to the worker.
  const frameRef = useRef(null);
  const printPayslip = () => {
    if (slip) downloadPayslipPdf(slip, slip.Period || `${MONTH_FULL[m] || month} ${year}`, company);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', maxWidth: 980, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => history.push('/my-payslips')} sx={{ textTransform: 'none', '@media print': { display: 'none' } }}>
          Kthehu te pagat
        </Button>
        {slip && (
          <Button variant="contained" size="small" startIcon={<PrintIcon fontSize="small" />} onClick={printPayslip} sx={{ '@media print': { display: 'none' } }}>
            Printo / PDF
          </Button>
        )}
      </Stack>

      {error && <Alert severity="info" sx={{ fontSize: '0.82rem', mb: 2 }}>{error}</Alert>}

      {!error && (slip
        ? <PayslipFrame ref={frameRef} html={html} />
        : (
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 2.5, textAlign: 'center', color: 'text.secondary' }}>
            Nuk u gjet fletëpagesë për {MONTH_FULL[m] || month} {year}.
          </Paper>
        ))}
    </Box>
  );
}

// ─── Yearly statistics — its own page (opened from the dashboard tab) ─────────
export function MyPayslipStatsPage() {
  const history = useHistory();
  const { year } = useParams();
  const { loading, error, payslips } = useMyPayslips();
  const y = Number(year);

  const monthSlips = useMemo(() => {
    const map = {};
    payslips.filter((s) => Number(s.Year) === y).forEach((s) => { if (s.Month) map[s.Month] = s; });
    return map;
  }, [payslips, y]);
  const hasData = Object.keys(monthSlips).length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => history.push('/my-payslips')} sx={{ textTransform: 'none', '@media print': { display: 'none' } }}>
          Kthehu te pagat
        </Button>
        {hasData && (
          <Button variant="outlined" size="small" startIcon={<PrintIcon fontSize="small" />} onClick={() => window.print()} sx={{ '@media print': { display: 'none' } }}>
            Printo / PDF
          </Button>
        )}
      </Stack>

      {error && <Alert severity="info" sx={{ fontSize: '0.82rem', mb: 2 }}>{error}</Alert>}

      {!error && (hasData
        ? <YearStats year={y} monthSlips={monthSlips} selMonth={null} onSelect={(mm) => history.push(`/my-payslips/${y}/${mm}`)} />
        : (
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 2.5, textAlign: 'center', color: 'text.secondary' }}>
            Nuk ka të dhëna pagese për vitin {year}.
          </Paper>
        ))}
    </Box>
  );
}
